/**
 * Iter239 · Command HQ Dashboard
 *
 * Mobile-first executive dashboard that leadership can open on a phone
 * during a 5-minute board update. Merges 4 signals from separate
 * subsystems into a single glanceable screen:
 *   1. Pipeline health (ACV, Hot/High counts, Missing Contact gap)
 *   2. Outreach signature winner (PT vs EN reply-rate delta)
 *   3. Enrichment ops (last cron sweep + coverage % + repository size)
 *   4. Tier breakdown (5 mini-cards with count + ACV per tier)
 *
 * Deliberate design decisions:
 *   • Single-column stack on <640px, 2-col ≥ 640px, 3-col ≥ 1024px.
 *   • Auto-refresh every 60s so an open browser tab is always live.
 *   • Zero dependencies on the heavy accordion/table components — pulls
 *     data from existing endpoints in parallel via Promise.all.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Shield, Flame, TrendingUp, DollarSign, MailWarning, Archive,
  Trophy, Zap, RefreshCw, Loader2, ArrowRight, CheckCircle2,
  AlertTriangle, Clock, MapPin, Users, AlertCircle, ChevronRight,
} from "lucide-react";
import ThemeToggle from "../../components/ThemeToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (v) => {
  const n = Number(v || 0);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000)      return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
};
const num = (v) => (v == null ? "—" : Number(v).toLocaleString("en-US"));
const timeAgo = (iso) => {
  if (!iso) return "—";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60)   return `${Math.round(diff)}s atrás`;
    if (diff < 3600) return `${Math.round(diff / 60)}m atrás`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h atrás`;
    return `${Math.round(diff / 86400)}d atrás`;
  } catch { return iso.slice(0, 10); }
};

const TIER_META = [
  { id: "hot",       label: "Hot",       icon: "🔥", color: "#DC2626" },
  { id: "high",      label: "High",      icon: "🟢", color: "#16A34A" },
  { id: "qualified", label: "Qualified", icon: "🟡", color: "#EAB308" },
  { id: "nurture",   label: "Nurture",   icon: "🔵", color: "#3B82F6" },
  { id: "low",       label: "Low",       icon: "⚪", color: "#94A3B8" },
];

export default function SmithHQ() {
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [missing, setMissing] = useState(null);
  const [sig, setSig] = useState(null);
  const [sweep, setSweep] = useState(null);
  const [repoTotal, setRepoTotal] = useState(0);
  // Iter240 · realtime via SSE + auto-enrich timeline
  const [live, setLive] = useState(false);
  const [timeline, setTimeline] = useState(null);
  // Iter249e · APIFY fallback rescue insights (last 7 days)
  const [rescues, setRescues] = useState(null);
  // Iter299 · BOI Compliance Tracker (FinCEN CTA) card feed.
  const [boi, setBoi] = useState(null);

  const boiCounts = useMemo(() => {
    const rows = (boi?.rows || []);
    return {
      overdue:     rows.filter((r) => r.status === "overdue").length,
      due_soon:    rows.filter((r) => r.status === "due_soon").length,
      on_track:    rows.filter((r) => r.status === "on_track").length,
      not_covered: rows.filter((r) => r.status === "not_covered").length,
      total:       rows.length,
      top_overdue: rows.filter((r) => r.status === "overdue").slice(0, 3),
    };
  }, [boi]);

  const load = async () => {
    setLoading(true);
    // Parallel fetch — a single slow endpoint doesn't stall the screen.
    const [d, m, s, sw, rp, rr, bo] = await Promise.allSettled([
      axios.get(`${API}/smith/dashboard`),
      axios.get(`${API}/smith/find-email/missing-contact-stats?min_acv=250000`),
      axios.get(`${API}/smith/outreach/analytics/signature?days=180`),
      axios.get(`${API}/smith/find-email/sweep-latest`),
      axios.get(`${API}/smith/find-email/archived?reason=no_email_found&limit=1`),
      axios.get(`${API}/smith/outreach/apify-fallback/insights?days=7`),
      axios.get(`${API}/smith/sunbiz/boi/dashboard?window=all&limit=200`),
    ]);
    if (d.status === "fulfilled")  setDashboard(d.value.data);
    if (m.status === "fulfilled")  setMissing(m.value.data);
    if (s.status === "fulfilled")  setSig(s.value.data);
    if (sw.status === "fulfilled") setSweep(sw.value.data);
    if (rp.status === "fulfilled") setRepoTotal(rp.value.data?.total || 0);
    if (rr.status === "fulfilled") setRescues(rr.value.data);
    if (bo.status === "fulfilled") setBoi(bo.value.data);
    setRefreshedAt(new Date().toISOString());
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Auto-refresh every 60s serves as a fallback when SSE stream is
    // interrupted (proxy timeout, tab backgrounded). SSE keeps the KPI
    // tiles in sync between refreshes.
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter240 · Auto-Enrich Timeline (last 7d feed with cost estimate).
  const loadTimeline = async () => {
    try {
      const r = await axios.get(`${API}/smith/find-email/auto-enrich-timeline?days=7`);
      setTimeline(r.data);
    } catch (_e) { /* silent — timeline is optional */ }
  };
  useEffect(() => { loadTimeline(); }, []);

  // Iter240 · Realtime push via SSE. Server emits one snapshot every ~5s
  // for up to 10 min then closes; we auto-reconnect on `onerror` after a
  // 3s backoff so we survive proxy hiccups without spamming reconnects.
  useEffect(() => {
    let es = null;
    let backoff = null;
    let closed = false;
    const connect = () => {
      try {
        es = new EventSource(`${API}/smith/hq/stream`, { withCredentials: true });
        es.addEventListener("hq", (evt) => {
          try {
            const payload = JSON.parse(evt.data || "{}");
            setLive(true);
            // Merge the SSE snapshot on top of the axios-loaded dashboard
            // so KPI tiles don't flicker between the two data sources.
            setDashboard((prev) => {
              const base = prev || {};
              return {
                ...base,
                totals: { ...(base.totals || {}), ...(payload.totals || {}) },
                pipeline_value: {
                  ...(base.pipeline_value || {}),
                  acv_sum: payload.totals?.acv_sum ?? base.pipeline_value?.acv_sum,
                },
              };
            });
            if (payload.sweep) setSweep(payload.sweep);
            if (payload.repo_total != null) setRepoTotal(payload.repo_total);
            setRefreshedAt(payload.at || new Date().toISOString());
          } catch (_e) { /* ignore malformed frame */ }
        });
        es.onerror = () => {
          setLive(false);
          try { es && es.close(); } catch (_e) { /* ignore */ }
          if (!closed) backoff = setTimeout(connect, 3000);
        };
      } catch (_e) { setLive(false); }
    };
    connect();
    return () => {
      closed = true;
      if (backoff) clearTimeout(backoff);
      try { es && es.close(); } catch (_e) { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aggregate per-tier counts from dashboard payload.
  const tierRows = useMemo(() => {
    const byTier = dashboard?.by_tier || {};
    return TIER_META.map((t) => ({
      ...t,
      count: byTier[t.id]?.count || 0,
      acv:   byTier[t.id]?.acv_sum || 0,
    }));
  }, [dashboard]);

  // Cron freshness — was the last sweep in the last 26h?
  const cronOk = useMemo(() => {
    const iso = sweep?.finished_at || sweep?.started_at;
    if (!iso) return null;
    try {
      const diff = (Date.now() - new Date(iso).getTime()) / 1000;
      return diff < 26 * 3600;
    } catch { return null; }
  }, [sweep]);

  const winner = sig?.winner;

  return (
    <div className="min-h-screen hq-scope"
         style={{ background: "var(--hq-page-bg)", color: "var(--hq-text)" }}
         data-testid="smith-hq-page">
      <div className="max-w-6xl mx-auto p-3 sm:p-5 space-y-4">

        {/* ── Header ─────────────────────────────────────────────── */}
        <header className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#DC2626 0%,#0F172A 100%)" }}>
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-[19px] sm:text-[22px] font-extrabold leading-tight">Command HQ</h1>
              <p className="text-[10.5px] sm:text-[11.5px] text-slate-400">
                Executive pulse · atualizado {timeAgo(refreshedAt)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              data-testid="hq-live-badge"
              title={live ? "SSE stream conectado" : "SSE offline · usando polling 60s"}
              className={`h-6 px-2 rounded-full text-[10px] font-extrabold uppercase tracking-widest inline-flex items-center gap-1 border ${
                live
                  ? "border-emerald-400/60 text-emerald-300 bg-emerald-500/10"
                  : "border-slate-500/40 text-slate-400 bg-slate-500/10"
              }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} />
              {live ? "Live" : "Offline"}
            </span>
            {/* Iter277 · Dark/Light toggle available on Command HQ too */}
            <ThemeToggle testid="hq-theme-toggle" className="!h-9" />
            <button type="button" onClick={load}
              disabled={loading}
              data-testid="hq-refresh-btn"
              className="h-9 px-3 rounded-lg border border-white/15 text-[12px] font-bold hover:border-[#DC2626] inline-flex items-center gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </header>

        {/* ── Row 1 · Pipeline Health (4 KPI tiles) ───────────────── */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-2.5"
                 data-testid="hq-pipeline-row">
          <KpiTile testid="hq-acv"
            icon={DollarSign} color="#EAB308"
            label="Pipeline ACV"
            value={usd(dashboard?.pipeline_value?.acv_sum || 0)}
            sub={`${num(dashboard?.totals?.prospects)} leads`} />
          <KpiTile testid="hq-hot"
            icon={Flame} color="#DC2626"
            label="Hot Prospects"
            value={num(dashboard?.totals?.hot)}
            sub={`+ ${num(dashboard?.totals?.high)} high`} />
          <KpiTile testid="hq-missing"
            icon={MailWarning}
            color={(missing?.missing_contact || 0) > 0 ? "#F59E0B" : "#10B981"}
            label="Missing Contact"
            value={`${num(missing?.missing_contact ?? 0)} / ${num(missing?.high_value_total ?? 0)}`}
            sub={missing ? `Coverage ${missing.coverage_pct}% · ACV≥$250k` : "carregando…"}
            highlight={(missing?.missing_contact || 0) > 0}
            href="/smith" />
          <KpiTile testid="hq-repo"
            icon={Archive}
            color={repoTotal > 0 ? "#F59E0B" : "#10B981"}
            label="Repositório"
            value={num(repoTotal)}
            sub="arquivados · retry ativo"
            href="/smith/repository" />
        </section>

        {/* Iter299 · BOI Compliance Tracker card — surfaces every FL
              prospect with a computed FinCEN Corporate Transparency Act
              status. Overdue items get a red pulse so masters see
              risk at a glance. */}
        {boi && boiCounts.total > 0 && (
          <section className="rounded-2xl border p-4 space-y-3"
                   data-testid="hq-boi-card"
                   style={{
                     background: boiCounts.overdue > 0
                                  ? "rgba(239, 68, 68, 0.10)"
                                  : "rgba(15, 23, 42, 0.7)",
                     borderColor: boiCounts.overdue > 0
                                    ? "rgba(239, 68, 68, 0.45)"
                                    : "rgba(245, 158, 11, 0.35)",
                   }}>
            <div className="flex items-center gap-2 flex-wrap">
              <AlertCircle className="w-4 h-4"
                style={{ color: boiCounts.overdue > 0 ? "#EF4444" : "#F59E0B" }} />
              <span className="text-[10.5px] uppercase tracking-widest font-extrabold"
                style={{ color: boiCounts.overdue > 0 ? "#FCA5A5" : "#FCD34D" }}>
                BOI · FinCEN Compliance
              </span>
              <span className="text-[9.5px] text-slate-400 font-normal">
                Corporate Transparency Act deadlines
              </span>
              <a href="/smith/prospects?boi=overdue"
                 data-testid="hq-boi-card-cta"
                 className="ml-auto text-[10.5px] font-bold text-sky-300 hover:text-sky-100 inline-flex items-center gap-1">
                Ver lista completa <ChevronRight className="w-3 h-3" />
              </a>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              <BoiTile label="Vencidos"      value={boiCounts.overdue}
                        color="#EF4444" testid="hq-boi-overdue" pulse />
              <BoiTile label="Vencem em 30d" value={boiCounts.due_soon}
                        color="#F59E0B" testid="hq-boi-due-soon" />
              <BoiTile label="Em dia"        value={boiCounts.on_track}
                        color="#10B981" testid="hq-boi-on-track" />
              <BoiTile label="Isentos"       value={boiCounts.not_covered}
                        color="#64748B" testid="hq-boi-not-covered" />
            </div>
            {boiCounts.top_overdue.length > 0 && (
              <div className="pt-2 border-t border-white/10 space-y-1"
                   data-testid="hq-boi-top-overdue">
                <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">
                  Top vencidos · atacar primeiro
                </div>
                {boiCounts.top_overdue.map((r) => (
                  <a key={r.prospect_id}
                     href={`/smith/prospects/${r.prospect_id}`}
                     className="flex items-center gap-2 rounded px-2 py-1 hover:bg-white/5 text-[11px]"
                     data-testid={`hq-boi-row-${r.prospect_id}`}>
                    <span className="w-1 h-1 rounded-full" style={{ background: "#EF4444" }} />
                    <span className="text-white font-bold truncate flex-1">
                      {r.company_name || "—"}
                    </span>
                    <span className="text-red-300 font-black tabular-nums">
                      {r.days_left < 0 ? `${-r.days_left}d atraso` : "vencendo"}
                    </span>
                    <ChevronRight className="w-3 h-3 text-slate-500" />
                  </a>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── Row 2 · Signature Winner + Cron Status ──────────────── */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">

          {/* Signature Winner card */}
          <div className="rounded-2xl border p-4 space-y-3"
               data-testid="hq-signature-card"
               style={{ background: "rgba(15,23,42,0.7)",
                          borderColor: "rgba(59,130,246,0.35)" }}>
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-blue-300" />
              <span className="text-[10.5px] uppercase tracking-widest text-blue-300 font-extrabold">
                Signature Winner · 180d
              </span>
              {sig && (
                <span className="ml-auto text-[10px] text-slate-500 tabular-nums">
                  {num(sig.total_sends)} envios
                </span>
              )}
            </div>
            {!sig ? (
              <div className="text-slate-500 text-[12px]">Carregando…</div>
            ) : winner && winner.delta_vs_other != null ? (
              <div className="flex items-center gap-4">
                <div className="text-[36px]">
                  {winner.lang === "pt" ? "🇧🇷" : "🇺🇸"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-extrabold text-white">
                    {winner.lang.toUpperCase()} vence
                  </div>
                  <div className="text-[11.5px] text-slate-400">
                    Reply-rate <b className="text-amber-300 tabular-nums">{winner.reply_rate}%</b>
                    {" "}·
                    Delta <b className={`tabular-nums ${winner.delta_vs_other >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {winner.delta_vs_other >= 0 ? "+" : ""}{winner.delta_vs_other}pp
                    </b>
                  </div>
                </div>
                <Link to="/smith/outreach?tab=analytics"
                  data-testid="hq-signature-drill"
                  className="text-[10.5px] font-bold text-blue-300 hover:text-white inline-flex items-center gap-1 shrink-0">
                  Detalhe <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ) : (
              <div className="text-[12px] text-slate-400">
                Sem dados suficientes ainda — precisa ≥ 5 envios em PT E EN para declarar vencedor
                {sig?.total_sends != null && <> · atualmente <b>{sig.total_sends}</b> envios total</>}
              </div>
            )}
            {sig?.by_language && sig.by_language.length > 0 && (
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
                {sig.by_language.map((r) => (
                  <div key={r.lang}
                       data-testid={`hq-lang-${r.lang}`}
                       className="rounded p-2 bg-black/30">
                    <div className="text-[10.5px] text-slate-400 font-bold uppercase">
                      {r.lang === "pt" ? "🇧🇷 PT" : "🇺🇸 EN"} · {r.sent} envios
                    </div>
                    <div className="grid grid-cols-3 gap-1 mt-1 tabular-nums">
                      <MiniStat label="Reply" v={`${r.reply_rate}%`} color="#F59E0B" />
                      <MiniStat label="Open" v={`${r.open_rate}%`} color="#3B82F6" />
                      <MiniStat label="Bounce" v={`${r.bounce_rate}%`} color="#EF4444" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cron / Enrichment Ops card */}
          <div className="rounded-2xl border p-4 space-y-3"
               data-testid="hq-cron-card"
               style={{ background: "rgba(15,23,42,0.7)",
                          borderColor: cronOk === false ? "rgba(239,68,68,0.4)"
                                        : "rgba(16,185,129,0.35)" }}>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4"
                    style={{ color: cronOk === false ? "#EF4444" : "#10B981" }} />
              <span className="text-[10.5px] uppercase tracking-widest font-extrabold"
                    style={{ color: cronOk === false ? "#F87171" : "#6EE7B7" }}>
                Enrichment Cron
              </span>
              <span className="ml-auto text-[10px] tabular-nums flex items-center gap-1"
                    style={{ color: cronOk === false ? "#F87171" : "#94A3B8" }}
                    data-testid="hq-cron-status">
                {cronOk === true && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                {cronOk === false && <AlertTriangle className="w-3 h-3 text-red-400" />}
                {cronOk === null && <Clock className="w-3 h-3 text-slate-400" />}
                {cronOk === true  ? "healthy"
                  : cronOk === false ? "stale (>26h)"
                  : "sem execução"}
              </span>
            </div>
            {sweep?.phase === "idle" || !sweep?.id ? (
              <div className="text-[12px] text-slate-400" data-testid="hq-cron-idle">
                Nenhuma execução registrada · cron nightly agendado 04:00 UTC
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2 tabular-nums">
                <MiniStat label="Processados" v={num(sweep.processed)} color="#94A3B8" />
                <MiniStat label="Recuperados" v={num(sweep.recovered)} color="#10B981" />
                <MiniStat label="Arquivados" v={num(sweep.archived)} color="#F59E0B" />
                <MiniStat label="Erros" v={num(sweep.errors)} color="#EF4444" />
              </div>
            )}
            {sweep?.started_at && (
              <div className="text-[10.5px] text-slate-500 flex items-center justify-between">
                <span>
                  Último: <b>{timeAgo(sweep.finished_at || sweep.started_at)}</b>
                  {sweep.triggered_by && <> · trigger: <span className="text-slate-400">{sweep.triggered_by}</span></>}
                </span>
                <Link to="/smith/prospects"
                      className="text-[10.5px] font-bold text-emerald-300 hover:text-white">
                  Rodar agora
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* ── Row 3 · Tier Breakdown ──────────────────────────────── */}
        <section className="rounded-2xl border p-3 sm:p-4 space-y-3"
                 data-testid="hq-tier-row"
                 style={{ background: "rgba(15,23,42,0.7)",
                            borderColor: "rgba(220,38,38,0.20)" }}>
          <div className="flex items-center justify-between">
            <span className="text-[10.5px] uppercase tracking-widest text-slate-400 font-extrabold">
              Distribuição por Tier
            </span>
            <Link to="/smith/prospects"
              data-testid="hq-tier-drill"
              className="text-[10.5px] font-bold text-slate-400 hover:text-white inline-flex items-center gap-1">
              Abrir cockpit <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {tierRows.map((t) => (
              <Link key={t.id}
                    to={`/smith/prospects?tier=${t.id}`}
                    data-testid={`hq-tier-${t.id}`}
                    className="rounded-lg border p-2.5 hover:bg-white/[0.03] transition-colors"
                    style={{ borderColor: `${t.color}55`,
                              background: `${t.color}0A` }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-[14px]">{t.icon}</span>
                  <span className="text-[10.5px] font-extrabold uppercase tracking-widest"
                        style={{ color: t.color }}>
                    {t.label}
                  </span>
                </div>
                <div className="text-[18px] font-extrabold tabular-nums text-white">
                  {num(t.count)}
                </div>
                <div className="text-[10px] text-emerald-300 font-bold tabular-nums">
                  {usd(t.acv)}
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ── Row 4 · Auto-Enrich Timeline (últimos 7 dias) ────────── */}
        <section className="rounded-2xl border p-3 sm:p-4 space-y-3"
                 data-testid="hq-timeline-card"
                 style={{ background: "rgba(15,23,42,0.7)",
                            borderColor: "rgba(59,130,246,0.25)" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-300" />
              <span className="text-[10.5px] uppercase tracking-widest text-blue-300 font-extrabold">
                Auto-Enrich Timeline · 7d
              </span>
            </div>
            {timeline && (
              <span className="text-[10.5px] text-slate-400 tabular-nums"
                    data-testid="hq-timeline-cost">
                {num(timeline.total_events)} eventos · custo est.{" "}
                <b className="text-amber-300">${timeline.total_min_cost}</b>–
                <b className="text-amber-300">${timeline.total_max_cost}</b>
              </span>
            )}
          </div>
          {!timeline ? (
            <div className="text-slate-500 text-[12px] flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando timeline…
            </div>
          ) : (timeline.days || []).length === 0 ? (
            <div className="text-[12px] text-slate-400" data-testid="hq-timeline-empty">
              Nenhum lead high-value auto-enriquecido nos últimos 7 dias · threshold ACV ≥ $250k
            </div>
          ) : (
            <div className="space-y-2">
              {timeline.days.slice(0, 7).map((d) => (
                <div key={d.date}
                     data-testid={`hq-timeline-day-${d.date}`}
                     className="rounded-lg border border-white/5 p-2.5 bg-black/30">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-extrabold text-slate-200 tabular-nums">
                      {d.date}
                    </span>
                    <span className="text-[10px] text-slate-400 tabular-nums">
                      <b className="text-white">{d.events}</b> lead{d.events !== 1 && "s"} ·{" "}
                      <b className="text-amber-300">${d.min_cost.toFixed(2)}</b>–
                      <b className="text-amber-300">${d.max_cost.toFixed(2)}</b>
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(d.sample || []).slice(0, 3).map((s) => (
                      <span key={s.prospect_id}
                            className="text-[10px] text-slate-300 bg-white/[0.03] rounded px-1.5 py-0.5 border border-white/5">
                        {s.company} · <span className="text-emerald-300 tabular-nums">{usd(s.acv_est)}</span>
                        {s.source && <span className="text-slate-500 ml-1">({s.source})</span>}
                      </span>
                    ))}
                    {d.events > (d.sample || []).length && (
                      <span className="text-[10px] text-slate-500">
                        +{d.events - (d.sample || []).length} mais
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── APIFY Fallback Rescues · 7d (Iter249e) ─────────────────────── */}
        {rescues && (
          <section className="rounded-2xl border border-white/10 p-4 sm:p-5"
                   data-testid="hq-apify-rescues-card"
                   style={{ background: "linear-gradient(135deg,rgba(6,95,70,0.15) 0%,rgba(15,23,42,0.7) 60%)" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-400" />
                <h3 className="font-extrabold text-white text-[13.5px] uppercase tracking-wider">
                  APIFY Rescues · {rescues.window_days}d
                </h3>
              </div>
              <span className="text-[10.5px] text-slate-400 tabular-nums"
                    data-testid="hq-rescues-cost">
                custo est. <b className="text-amber-300">${rescues.estimated_cost_usd.toFixed(2)}</b>
                {rescues.totals.total_rescues > 0 && (
                  <> · <b className="text-emerald-300">${rescues.cost_per_rescue_usd}</b>/rescue</>
                )}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {[
                { key: "website", label: "Website", color: "#10B981", desc: "Emails do site do lead" },
                { key: "gmaps", label: "Google Maps", color: "#3B82F6", desc: "Contatos do Places" },
                { key: "linkedin", label: "LinkedIn", color: "#0EA5E9", desc: "Perfis de decisores" },
              ].map(({ key, label, color, desc }) => (
                <div key={key} className="rounded-lg border border-white/10 p-3 bg-black/40"
                     data-testid={`hq-rescue-${key}`}>
                  <div className="text-[9.5px] uppercase tracking-widest font-bold"
                       style={{ color }}>
                    {label}
                  </div>
                  <div className="text-[22px] font-extrabold text-white tabular-nums leading-tight">
                    {rescues.totals[key]}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{desc}</div>
                </div>
              ))}
            </div>
            {rescues.totals.total_rescues === 0 && (
              <div className="text-[11px] text-slate-500 mt-3"
                   data-testid="hq-rescues-empty">
                Nenhum lead resgatado pelo fallback APIFY nos últimos {rescues.window_days} dias — Apollo/Hunter cobriram tudo.
              </div>
            )}
          </section>
        )}

        {/* ── Footer · deep links (Iter249 · Dashboard link removed — this
             page IS the dashboard, so the tab was pointing at itself.) */}
        <footer className="grid grid-cols-3 gap-2 pt-2"
                data-testid="hq-footer-nav">
          <FooterLink to="/smith/prospects" label="Prospects" testid="hq-link-prospects" />
          <FooterLink to="/smith/outreach" label="Outreach" testid="hq-link-outreach" />
          <FooterLink to="/smith/map" label="Map" testid="hq-link-map" />
        </footer>
      </div>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────

function KpiTile({ icon: Icon, color, label, value, sub, testid, href, highlight }) {
  const body = (
    <div
      className="rounded-xl border p-3 h-full"
      data-testid={testid}
      style={{
        background: "var(--hq-card-bg)",
        borderColor: highlight ? `${color}80` : "var(--hq-border)",
      }}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span className="text-[9.5px] uppercase tracking-widest font-extrabold"
              style={{ color }}>
          {label}
        </span>
      </div>
      <div className="text-[20px] sm:text-[22px] font-extrabold tabular-nums mt-1">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-400 truncate">{sub}</div>}
    </div>
  );
  return href
    ? <Link to={href} className="block h-full transition-transform hover:-translate-y-0.5">{body}</Link>
    : body;
}

function MiniStat({ label, v, color }) {
  return (
    <div className="text-center">
      <div className="text-[8.5px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="text-[12.5px] font-extrabold tabular-nums" style={{ color }}>{v}</div>
    </div>
  );
}

function FooterLink({ to, label, testid }) {
  return (
    <Link to={to}
      data-testid={testid}
      className="text-center h-9 rounded-lg border border-white/10 text-[11.5px] font-bold text-slate-300 hover:text-white hover:border-white/30 flex items-center justify-center">
      {label}
    </Link>
  );
}


// Iter299 · Compact tile for the BOI Compliance card.
function BoiTile({ label, value, color, testid, pulse }) {
  return (
    <div className="rounded-lg border px-3 py-2.5 flex items-center gap-3"
         data-testid={testid}
         style={{
           background: "rgba(15, 23, 42, 0.4)",
           borderColor: `${color}55`,
         }}>
      <div className={`w-2.5 h-2.5 rounded-full ${pulse && value > 0 ? "animate-pulse" : ""}`}
           style={{ background: value > 0 ? color : "#334155" }} />
      <div className="flex-1 min-w-0">
        <div className="text-[9.5px] uppercase tracking-widest font-bold"
             style={{ color: value > 0 ? color : "#94A3B8" }}>
          {label}
        </div>
        <div className="text-[18px] font-black tabular-nums text-white">{value}</div>
      </div>
    </div>
  );
}
